import {
  Component,
  OnInit,
  Inject,
  ChangeDetectorRef,
  AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ComponentDataDestService } from '../../../services/component-data-dest/component-data-dest.service';
import { PdfDownloadService } from '../../../services/duna/report/pdf-download.service';
import { AdviDTO } from '../../../dto/advi/advi.dto';
import { createDefaultListRequest } from '../../../dto/list-request.dto';
import { ListResponseDTO } from '../../../dto/list-response.dto';
import { environment } from '../../../environment/environment';
import { DunaReportWizardComponent } from '../duna-report-wizard/duna-report-wizard.component';
import { ReportWizardContext } from '../duna-report-wizard/report-wizard-context';
import { EducationalPathDTO } from '../../../dto/educational-path/educational-path.dto';

@Component({
  selector: 'app-duna-advi',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCardModule,
    MatChipsModule,
    MatTooltipModule,
    DunaReportWizardComponent,
  ],
  templateUrl: './duna-advi.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-advi.component.scss'],
})
export class DunaAdviComponent implements OnInit, AfterViewInit {
  showWizard = false;
  wizardContext: ReportWizardContext = { mode: 'organization' };

  advisories: AdviDTO[] = [];
  availablePaths: EducationalPathDTO[] = [];
  displayedColumns = [
    'name',
    'paths',
    'recipients',
    'intervals',
    'status',
    'lastSent',
    'actions',
  ];
  readonly maxVisiblePaths = 5;
  private expandedPathRows = new Set<number>();

  constructor(
    @Inject(ComponentDataSourceService)
    private dataSource: ComponentDataSourceService,
    @Inject(ComponentDataDestService)
    private dataDest: ComponentDataDestService,
    private pdfDownload: PdfDownloadService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.loadAdvisories();
      this.loadPaths();
    }, 200);
  }

  loadAdvisories(): void {
    this.dataSource
      ?.executeDataSource('advisories', {
        listRequest: createDefaultListRequest(),
      })
      ?.subscribe({
        next: (res: ListResponseDTO<AdviDTO>) => {
          this.advisories = res.data;
          this.cdr.detectChanges();
        },
        error: (err: unknown) =>
          console.error('[DunaAdviComponent] loadAdvisories', err),
      });
  }

  loadPaths(): void {
    const req = createDefaultListRequest();
    req.pageSize = 10000;
    this.dataSource
      ?.executeDataSource('educational_paths', { listRequest: req })
      ?.subscribe({
        next: (res: any) => {
          this.availablePaths = res.data ?? res.items ?? [];
          this.cdr.detectChanges();
        },
      });
  }

  startCreateWizard(): void {
    this.wizardContext = { mode: 'organization' };
    this.showWizard = true;
  }

  onEdit(advisory: AdviDTO): void {
    this.wizardContext = DunaReportWizardComponent.contextFromAdvi(advisory);
    this.showWizard = true;
  }

  onWizardCancelled(): void {
    this.showWizard = false;
  }

  onWizardSaved(): void {
    this.showWizard = false;
    this.loadAdvisories();
  }

  toggleAdvisory(advisory: AdviDTO): void {
    this.dataDest
      .getDataDest('toggle_advisory')
      ?.call(null, advisory.advi_id!)
      .subscribe({
        next: () => this.loadAdvisories(),
        error: () => alert('Fejl ved ændring af status.'),
      });
  }

  deleteAdvisory(advisory: AdviDTO): void {
    if (!confirm(`Slet "${advisory.name}"?`)) return;
    this.dataDest
      .getDataDest('delete_advisory')
      ?.call(null, advisory.advi_id!)
      .subscribe({
        next: () => this.loadAdvisories(),
      });
  }

  sendAdvisoryNow(advisory: AdviDTO): void {
    const conf = advisory.report_config as any;
    const adviseringOn =
      conf?.delivery?.advisering_enabled ??
      advisory.recipient_user_ids?.length > 0;
    if (!adviseringOn) {
      alert('Advisering er ikke aktiveret for denne rapport.');
      return;
    }
    if (!confirm(`Send "${advisory.name}" nu?`)) return;
    this.dataDest
      .getDataDest('send_advisory_now')
      ?.call(null, advisory.advi_id!)
      .subscribe({
        next: () => {
          alert('Sendt!');
          this.loadAdvisories();
        },
        error: () => alert('Fejl ved afsendelse.'),
      });
  }

  downloadAdvisoryPDF(advisory: AdviDTO): void {
    const url = `${environment.apiUrl}/advi/download-pdf/${advisory.advi_id}`;
    this.dataSource.http
      .get<{
        success: boolean;
        pdf_content?: string;
        filename?: string;
        message?: string;
      }>(url)
      .subscribe({
        next: (data) => {
          if (data.success && data.pdf_content) {
            this.pdfDownload.downloadFromJsonResponse(data);
          } else {
            alert(data.message ?? 'Ingen PDF returneret');
          }
        },
        error: () => alert('Fejl ved download af PDF.'),
      });
  }

  getPathName(pid: number): string {
    const p = this.availablePaths.find((x) => x.path_id === pid);
    // `||` (not `??`) so an empty or whitespace-only name falls back instead of
    // rendering an empty chip.
    return (
      (p && (p.name?.trim() || p.full_name?.trim())) || `Path ${pid}`
    );
  }

  visiblePathIds(advisory: AdviDTO): number[] {
    const ids = advisory.path_ids ?? [];
    if (this.isPathsExpanded(advisory) || ids.length <= this.maxVisiblePaths) {
      return ids;
    }
    return ids.slice(0, this.maxVisiblePaths);
  }

  hiddenPathCount(advisory: AdviDTO): number {
    return Math.max(0, (advisory.path_ids?.length ?? 0) - this.maxVisiblePaths);
  }

  isPathsExpanded(advisory: AdviDTO): boolean {
    return (
      advisory.advi_id != null && this.expandedPathRows.has(advisory.advi_id)
    );
  }

  togglePathsExpanded(advisory: AdviDTO, event?: Event): void {
    event?.stopPropagation();
    if (advisory.advi_id == null) return;
    if (this.expandedPathRows.has(advisory.advi_id)) {
      this.expandedPathRows.delete(advisory.advi_id);
    } else {
      this.expandedPathRows.add(advisory.advi_id);
    }
  }

  getReportTypeLabel(advi: AdviDTO): string {
    const labels: Record<string, string> = {
      event_report: 'Status',
      module_participation: 'Moduldeltagelse',
      participation_snapshot: 'Snapshot',
      user_progress_simple: 'Bruger-fremgang',
      module_matrix_simple: 'Modul-oversigt',
      lagging_only: 'Efterslæb',
      merit_summary: 'Merit',
      minimal_onepager: 'Minimal',
    };
    return labels[advi.report_type] ?? advi.report_type;
  }

  hasAdvisering(advi: AdviDTO): boolean {
    const conf = advi.report_config as any;
    if (conf?.delivery?.advisering_enabled !== undefined)
      return conf.delivery.advisering_enabled;
    return (advi.recipient_user_ids?.length ?? 0) > 0;
  }

  getIntervalLabel(advi: AdviDTO): string {
    if (!this.hasAdvisering(advi)) {
      const conf = advi.report_config as any;
      const date = conf?.delivery?.date ?? conf?.date;
      if (date?.mode === 'range') return `${date.date_from} – ${date.date_to}`;
      return date?.year?.toString() ?? '—';
    }
    const conf = advi.report_config as any;
    const scheduleMode = conf?.delivery?.schedule_mode ?? 'before_expiry';
    if (scheduleMode === 'calendar') {
      const cal =
        conf?.delivery?.calendar_interval ?? advi.advisory_intervals?.[0];
      if (!cal?.value) return '—';
      return `Hver ${cal.value} ${this.getIntervalUnitDanish(
        cal.unit,
        cal.value
      )}`;
    }
    if (!advi.advisory_intervals?.length) return '—';
    const iv = advi.advisory_intervals[0];
    return `${iv.value} ${this.getIntervalUnitDanish(
      iv.unit,
      iv.value
    )} før udløb`;
  }

  getIntervalUnitDanish(unit: string, value: number): string {
    const map: Record<string, string> = {
      days: 'dag',
      weeks: 'uge',
      months: 'måned',
    };
    const base = map[unit] ?? unit;
    return value === 1 ? base : base + 'e';
  }
}
